import React from 'react';
import { useDispatch } from 'react-redux';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ClosingDateNotice } from './ClosingDateNotice';
import { formatLongBusinessDatePt } from '../../../slices/marketplace/timeline/utils/timeline-dates';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import { ConfirmClose } from '../../../slices/marketplace/closing-date/thunks/confirm-close.thunk';
import { ClosingDateCloseConfirmation } from '../../../slices/marketplace/closing-date/models/engagement-closing-date.model';
import {
    TimelineParty,
    getPartyName,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';

const PARTY_DISPLAY_ORDER = [TimelineParty.Buyer, TimelineParty.Seller];

export interface ClosingDateCloseConfirmationSectionProps {
    engagementId: number;
    closeConfirmations: ClosingDateCloseConfirmation[];
    currentUserParty?: TimelineParty | null;
    canConfirmClose: boolean;
    officialClosingDate?: string | null;
}

export const ClosingDateCloseConfirmationSection = ({
    engagementId,
    closeConfirmations,
    currentUserParty,
    canConfirmClose,
    officialClosingDate,
}: ClosingDateCloseConfirmationSectionProps) => {
    const dispatch = useDispatch() as any;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );

    const handleConfirmClose = () => {
        if (isSubmitting) {
            return;
        }
        dispatch(ConfirmClose(engagementId));
    };

    const orderedConfirmations = PARTY_DISPLAY_ORDER.map((party) =>
        closeConfirmations.find((confirmation) => confirmation.party === party)
    ).filter(
        (confirmation): confirmation is ClosingDateCloseConfirmation =>
            !!confirmation
    );

    const bothConfirmed =
        orderedConfirmations.length === PARTY_DISPLAY_ORDER.length &&
        orderedConfirmations.every((confirmation) => confirmation.isConfirmed);

    return (
        <div className="closing-date-close-confirmation">
            <div className="closing-date-close-confirmation__title">
                Confirm the close occurred
            </div>
            <div className="closing-date-close-confirmation__description">
                Confirm the close on your side once the transaction has
                completed for the agreed closing date of{' '}
                {formatLongBusinessDatePt(officialClosingDate)}. The Buyer
                confirms separately from their own dashboard. The engagement is
                marked closed when both parties confirm.
            </div>
            <div className="closing-date-close-confirmation__panels">
                {orderedConfirmations.map((confirmation) => {
                    const isYou = confirmation.party === currentUserParty;
                    return (
                        <div
                            key={confirmation.party}
                            className={`closing-date-close-confirmation__panel${
                                confirmation.isConfirmed
                                    ? ' closing-date-close-confirmation__panel--confirmed'
                                    : ''
                            }`}>
                            <div className="closing-date-close-confirmation__panel-info">
                                <div className="closing-date-close-confirmation__panel-label">
                                    {getPartyName(confirmation.party)}
                                    {isYou && (
                                        <span className="closing-date-close-confirmation__panel-you">
                                            {' '}
                                            (you)
                                        </span>
                                    )}
                                </div>
                                <div className="closing-date-close-confirmation__panel-status">
                                    {confirmation.isConfirmed
                                        ? 'Confirmed close'
                                        : 'Awaiting confirmation'}
                                </div>
                            </div>
                            {confirmation.isConfirmed ? (
                                <span className="badge badge-pill badge-success">
                                    Confirmed
                                </span>
                            ) : isYou && canConfirmClose ? (
                                <ButtonLoader
                                    buttonText="Confirm close"
                                    loaderButtonText="Confirm close"
                                    isLoading={isSubmitting}
                                    disabled={isSubmitting}
                                    onClick={handleConfirmClose}
                                    className="btn btn-primary"
                                />
                            ) : isYou ? null : (
                                <span className="badge badge-pill badge-warning">
                                    Pending
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            {bothConfirmed && (
                <ClosingDateNotice
                    tone="success"
                    icon={faCheck}
                    className="closing-date-close-confirmation__alert">
                    Both parties have confirmed the close. This engagement is
                    now marked as closed.
                </ClosingDateNotice>
            )}
        </div>
    );
};
